import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getUserDeletePreview,
  type UserAccount,
  type DeletePreview,
} from '../../../shared/api/users.api'
import { ROLE_LABELS } from './UserRow'
import { PageLoader } from '../../../shared/ui/Spinner'

interface Props {
  account: UserAccount
  onClose: () => void
  onConfirm: (transferTo: string | undefined) => void
  saving: boolean
  error: unknown
}

function CountRow({ label, value }: { label: string; value: number }) {
  if (value === 0) return null
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  )
}

function PreviewContent({
  preview,
  account,
  transferTo,
  setTransferTo,
  confirmText,
  setConfirmText,
}: {
  preview: DeletePreview
  account: UserAccount
  transferTo: string
  setTransferTo: (v: string) => void
  confirmText: string
  setConfirmText: (v: string) => void
}) {
  const { counts, needsTransfer, canDelete, blockReason, candidates } = preview
  const allOrphanedZero =
    counts.quotes +
      counts.quotations +
      counts.comments +
      counts.statusHistory +
      counts.itemStatusHistory +
      counts.requestEvents ===
    0
  const expectedConfirm = account.email

  return (
    <>
      <div className="bg-gray-50 rounded-md p-3 space-y-1">
        <div className="text-sm text-gray-700">
          <span className="font-medium">{account.name || account.email}</span>{' '}
          <span className="text-gray-500">({ROLE_LABELS[account.role] ?? account.role})</span>
        </div>
        <div className="text-xs text-gray-500">{account.email}</div>
      </div>

      {!canDelete && blockReason && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {blockReason}
        </div>
      )}

      {canDelete && (
        <>
          {needsTransfer && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
              У этого аккаунта есть клиенты и заявки. Выберите кому передать — иначе удаление
              невозможно.
            </div>
          )}

          <div className="border border-gray-200 rounded-md p-3 space-y-0.5">
            <div className="text-xs uppercase text-gray-500 mb-2 font-medium">Связанные данные</div>
            {counts.clients === 0 && counts.requests === 0 && allOrphanedZero ? (
              <div className="text-sm text-gray-500">Нет связанных данных</div>
            ) : (
              <>
                {needsTransfer && (
                  <>
                    <div className="text-xs uppercase text-gray-400 mt-1 mb-1">
                      Будет передано получателю
                    </div>
                    <CountRow label="Клиенты" value={counts.clients} />
                    <CountRow label="Заявки" value={counts.requests} />
                  </>
                )}
                {!allOrphanedZero && (
                  <>
                    <div className="text-xs uppercase text-gray-400 mt-2 mb-1">
                      Сохранится в системе (автор «удалённый пользователь»)
                    </div>
                    <CountRow label="Квоты (созданы)" value={counts.quotes} />
                    <CountRow label="КП (сформированы)" value={counts.quotations} />
                    <CountRow label="Комментарии" value={counts.comments} />
                    <CountRow label="Записи истории заявок" value={counts.statusHistory} />
                    <CountRow label="Записи истории позиций" value={counts.itemStatusHistory} />
                    <CountRow label="События заявок" value={counts.requestEvents} />
                  </>
                )}
              </>
            )}
          </div>

          {needsTransfer && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Передать кому:</label>
              <select
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              >
                <option value="">— выберите получателя —</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.name || c.email) + ` · ${ROLE_LABELS[c.role] ?? c.role}`}
                  </option>
                ))}
              </select>
              {candidates.length === 0 && (
                <div className="text-xs text-red-600">
                  Нет доступных получателей. Создайте менеджера или РОПа.
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Подтверждение: введите email удаляемого аккаунта{' '}
              <span className="text-gray-500">({expectedConfirm})</span>
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              autoComplete="off"
            />
          </div>
        </>
      )}
    </>
  )
}

export function DeleteAccountModal({ account, onClose, onConfirm, saving, error }: Props) {
  const [transferTo, setTransferTo] = useState('')
  const [confirmText, setConfirmText] = useState('')

  const { data: preview, isLoading } = useQuery({
    queryKey: ['user-delete-preview', account.id],
    queryFn: () => getUserDeletePreview(account.id),
  })

  const expectedConfirm = account.email
  const confirmMatches = confirmText.trim() === expectedConfirm

  const canSubmit =
    preview?.canDelete && confirmMatches && (!preview.needsTransfer || transferTo) && !saving

  const errorMsg = (() => {
    if (!error) return null
    const e = error as { response?: { data?: { error?: { message?: string } } }; message?: string }
    return e?.response?.data?.error?.message || e?.message || 'Ошибка удаления'
  })()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Удаление аккаунта</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
          {isLoading || !preview ? (
            <PageLoader />
          ) : (
            <PreviewContent
              preview={preview}
              account={account}
              transferTo={transferTo}
              setTransferTo={setTransferTo}
              confirmText={confirmText}
              setConfirmText={setConfirmText}
            />
          )}

          {errorMsg && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={() => onConfirm(preview?.needsTransfer ? transferTo : undefined)}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Удаление…' : 'Удалить'}
          </button>
        </div>
      </div>
    </div>
  )
}
